import { useState, useEffect } from 'react';
import { FaCheck, FaTimes, FaArrowLeft, FaArrowRight, FaRedo, FaTerminal, FaClock, FaChartBar, FaAward, FaChevronRight } from 'react-icons/fa';
import { motion, AnimatePresence } from 'framer-motion';
import { useLocation } from 'react-router-dom';

type Question = {
    id: number;
    text: string;
    options: Option[];
    correctAnswer: string;
};

type Option = {
    id: string;
    text: string;
};

const ExamTerminal = () => {
    const location = useLocation();
    const [currentQuestionIndex, setCurrentQuestionIndex] = useState<number>(0);
    const [selectedOption, setSelectedOption] = useState<string | null>(null);
    const [isSubmitted, setIsSubmitted] = useState<boolean>(false);
    const [score, setScore] = useState<number>(0);
    const [timeLeft, setTimeLeft] = useState<number>(60);
    const [isLoading, setIsLoading] = useState<boolean>(true);
    const [examCompleted, setExamCompleted] = useState<boolean>(false);
    const [showResults, setShowResults] = useState<boolean>(false);
    const [userAnswers, setUserAnswers] = useState<Record<number, { answer: string; submitted: boolean }>>({});

    const questions: Question[] = [
        {
            id: 1,
            text: "Who was the first Prime Minister of India?",
            options: [
                { id: 'A', text: 'Mahatma Gandhi' },
                { id: 'B', text: 'Jawaharlal Nehru' },
                { id: 'C', text: 'Sardar Patel' },
                { id: 'D', text: 'Rajendra Prasad' }
            ],
            correctAnswer: 'B',
        },
        {
            id: 2,
            text: "Which is the national flower of India?",
            options: [
                { id: 'A', text: 'Rose' },
                { id: 'B', text: 'Sunflower' },
                { id: 'C', text: 'Lotus' },
                { id: 'D', text: 'Lily' }
            ],
            correctAnswer: 'C',
        },
        {
            id: 3,
            text: "Which Indian state is known as the 'Land of Five Rivers'?",
            options: [
                { id: 'A', text: 'Punjab' },
                { id: 'B', text: 'Rajasthan' },
                { id: 'C', text: 'Haryana' },
                { id: 'D', text: 'Uttar Pradesh' }
            ],
            correctAnswer: 'A',
        },
        {
            id: 4,
            text: "Who wrote the Indian national anthem?",
            options: [
                { id: 'A', text: 'Rabindranath Tagore' },
                { id: 'B', text: 'Bankim Chandra Chattopadhyay' },
                { id: 'C', text: 'Sarojini Naidu' },
                { id: 'D', text: 'Subhash Chandra Bose' }
            ],
            correctAnswer: 'A',
        },
        {
            id: 5,
            text: "Which city is known as the 'Silicon Valley of India'?",
            options: [
                { id: 'A', text: 'Hyderabad' },
                { id: 'B', text: 'Chennai' },
                { id: 'C', text: 'Bengaluru' },
                { id: 'D', text: 'Mumbai' }
            ],
            correctAnswer: 'C',
        },
    ];


    useEffect(() => {
        const timer = setTimeout(() => setIsLoading(false), 5000);
        return () => clearTimeout(timer);
    }, []);

    useEffect(() => {
        if (!isLoading && !examCompleted) {
            const timer = setInterval(() => {
                setTimeLeft(prev => {
                    if (prev <= 1) {
                        clearInterval(timer);
                        handleAutoSubmitExam();
                        return 0;
                    }
                    return prev - 1;
                });
            }, 1000);
            return () => clearInterval(timer);
        }
    }, [isLoading, examCompleted]);

    useEffect(() => {
        const currentQuestionId = questions[currentQuestionIndex].id;
        setSelectedOption(userAnswers[currentQuestionId]?.answer || null);
        setIsSubmitted(userAnswers[currentQuestionId]?.submitted || false);
    }, [currentQuestionIndex]);

    const handleAutoSubmitExam = () => {
        if (!examCompleted) {
            const updatedAnswers = { ...userAnswers };
            questions.forEach(question => {
                if (!updatedAnswers[question.id]) {
                    updatedAnswers[question.id] = { answer: '', submitted: true };
                } else {
                    updatedAnswers[question.id].submitted = true;
                }
            });
            setUserAnswers(updatedAnswers);

            let calculatedScore = 0;
            questions.forEach(question => {
                if (updatedAnswers[question.id]?.answer === question.correctAnswer) {
                    calculatedScore++;
                }
            });
            setScore(calculatedScore);
            setExamCompleted(true);
            setShowResults(true);
        }
    };

    const handleOptionSelect = (optionId: string) => {
        if (!isSubmitted && !examCompleted) {
            setSelectedOption(optionId);
            setUserAnswers(prev => ({
                ...prev,
                [currentQuestion.id]: { answer: optionId, submitted: false }
            }));
        }
    };


    const handleQuestionChange = (direction: 'prev' | 'next') => {
        if (direction === 'prev' && currentQuestionIndex > 0) {
            setCurrentQuestionIndex(currentQuestionIndex - 1);
        } else if (direction === 'next' && currentQuestionIndex < questions.length - 1) {
            setCurrentQuestionIndex(currentQuestionIndex + 1);
        }
    };

    // const resetExam = () => {
    //     setCurrentQuestionIndex(0);
    //     setSelectedOption(null);
    //     setIsSubmitted(false);
    //     setScore(0);
    //     setTimeLeft(60);
    //     setExamCompleted(false);
    //     setShowResults(false);
    //     setUserAnswers({});
    // };

    const resetExam = () => {
        // First show loading state
        setIsLoading(true);

        // Reset all exam state after a short delay (for better UX)
        setTimeout(() => {
            setCurrentQuestionIndex(0);
            setSelectedOption(null);
            setIsSubmitted(false);
            setScore(0);
            setTimeLeft(60);
            setExamCompleted(false);
            setShowResults(false);
            setUserAnswers({});
            // Hide loading after state is reset
            setIsLoading(false);
        }, 1000); // 1 second loading delay
    };


    // Update these lines in your component (around line 120-130)
    const handleSaveAnswer = () => {
        if (selectedOption) {
            setIsSubmitted(true);
            setUserAnswers(prev => ({
                ...prev,
                [currentQuestion.id]: { answer: selectedOption, submitted: true }
            }));
        }
    };

    const handleSubmitExam = () => {
        // Calculate final score
        let calculatedScore = 0;
        questions.forEach(question => {
            if (userAnswers[question.id]?.answer === question.correctAnswer) {
                calculatedScore++;
            }
        });

        setScore(calculatedScore);
        setExamCompleted(true);
        setShowResults(true);
    };

    // Update these conditions
    const currentQuestion = questions[currentQuestionIndex];
    const isLastQuestion = currentQuestionIndex === questions.length - 1;
    const currentAnswer = userAnswers[currentQuestion.id];
    const showSaveButton = selectedOption && !currentAnswer?.submitted;
    const showNextButton = currentAnswer?.submitted && !isLastQuestion;
    const showSubmitButton = isLastQuestion && currentAnswer?.submitted;
    const allQuestionsAnswered = questions.every(q => userAnswers[q.id]?.submitted);


    const currentUrl = `${window.location.origin}${location.pathname}`;

    if (isLoading) {
        return (
            <div className="h-auto min-h-[28rem] flex flex-col justify-center items-center bg-gradient-to-br from-gray-900 to-gray-800 rounded-xl border border-gray-700 shadow-2xl">
                {/* Animated logo/icon */}
                <motion.div
                    initial={{ scale: 0.8, opacity: 0 }}
                    animate={{ scale: 1, opacity: 1 }}
                    transition={{ duration: 0.5 }}
                    className="relative w-24 h-24 mb-6"
                >
                    {/* Outer ring */}
                    <motion.div
                        className="absolute inset-0 border-4 border-transparent border-t-blue-500 rounded-full"
                        animate={{ rotate: 360 }}
                        transition={{ duration: 2, repeat: Infinity, ease: "linear" }}
                    />

                    {/* Inner ring */}
                    <motion.div
                        className="absolute inset-4 border-4 border-transparent border-b-indigo-500 rounded-full"
                        animate={{ rotate: -360 }}
                        transition={{ duration: 1.5, repeat: Infinity, ease: "linear" }}
                    />

                    {/* Center icon */}
                    <div className="absolute inset-6 flex items-center justify-center">
                        <FaTerminal className="text-3xl text-blue-400" />
                    </div>
                </motion.div>

                {/* Loading text with typing animation */}
                <div className="text-center px-4">
                    <motion.h3
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        transition={{ delay: 0.3 }}
                        className="text-lg sm:text-xl font-medium text-gray-100 mb-1 sm:mb-2"
                    >
                        Preparing Your Exam
                    </motion.h3>
                    <motion.p
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 0.7 }}
                        transition={{ delay: 0.5 }}
                        className="text-gray-400 text-xs sm:text-sm"
                    >
                        Loading questions and setting up environment
                    </motion.p>
                </div>

                {/* Progress bar with gradient */}
                <motion.div
                    className="w-64 h-2 bg-gray-800 rounded-full mt-8 overflow-hidden"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ delay: 0.7 }}
                >
                    <motion.div
                        className="h-full bg-gradient-to-r from-blue-500 via-indigo-500 to-purple-500"
                        initial={{ x: "-100%" }}
                        animate={{ x: "100%" }}
                        transition={{
                            duration: 2,
                            repeat: Infinity,
                            ease: "linear"
                        }}
                        style={{
                            width: "100%"
                        }}
                    />
                </motion.div>

                {/* Status text */}
                <motion.div
                    className="mt-4 text-xs text-gray-500"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ delay: 1 }}
                >
                    <span className="inline-block mr-2">●</span>
                    <span>Securing connection</span>
                </motion.div>
            </div>
        );
    }

    if (examCompleted && showResults) {
        const percentage = Math.round((score / questions.length) * 100);

        return (
            <div className="h-auto min-h-[28rem] max-w-2xl mx-auto bg-gradient-to-br from-gray-900 to-gray-800 rounded-xl border border-gray-700 overflow-hidden shadow-2xl flex flex-col">
                <div className="flex items-center px-4 md:px-6 py-3 md:py-4 bg-gray-800/50 border-b border-gray-700 z-50">
                    <div className="flex space-x-2 mr-3 md:mr-4">
                        <div className="w-2 h-2 md:w-3 md:h-3 rounded-full bg-red-500"></div>
                        <div className="w-2 h-2 md:w-3 md:h-3 rounded-full bg-yellow-500"></div>
                        <div className="w-2 h-2 md:w-3 md:h-3 rounded-full bg-green-500"></div>
                    </div>
                    <div className="text-xs md:text-sm text-gray-300 font-mono flex items-center truncate">
                        <FaTerminal className="mr-1 md:mr-2 text-blue-400" />
                        <span className="text-gray-500 truncate">{currentUrl}</span>
                        <span className="text-gray-500 truncate">exam/q{currentQuestionIndex + 1}</span>
                    </div>
                    <div className="ml-auto flex items-center space-x-2 md:space-x-4">
                        <div className="text-xs text-gray-500 font-mono hidden sm:flex items-center">
                            {new Date().toLocaleTimeString('en-US', {
                                hour: '2-digit',
                                minute: '2-digit',
                                second: '2-digit',
                                hour12: true
                            })}
                        </div>


                    </div>
                </div>

                <div className="p-4 sm:p-6 flex-grow flex flex-col items-center justify-center">
                    <motion.div
                        initial={{ scale: 0.8, opacity: 0 }}
                        animate={{ scale: 1, opacity: 1 }}
                        transition={{ duration: 0.5 }}
                        className="w-full"
                    >
                        <div className="text-center mb-6 sm:mb-8">
                            <h2 className="text-2xl sm:text-3xl font-bold text-gray-100 mb-1 sm:mb-2">Exam Results</h2>
                            <p className="text-sm sm:text-base text-gray-400">You've completed the exam</p>
                        </div>

                        <div className="grid grid-cols-3 gap-3 sm:gap-4 mb-6 sm:mb-8">
                            <div className="bg-gray-800/50 border border-gray-700 rounded-lg sm:rounded-xl p-2 sm:p-4 text-center">
                                <div className="text-blue-400 mb-1 flex justify-center">
                                    <FaChartBar size={16} className="sm:size-5" />
                                </div>
                                <div className="text-xl sm:text-2xl font-bold text-gray-100">{score}/{questions.length}</div>
                                <div className="text-xs text-gray-400">Score</div>
                            </div>
                            <div className="bg-gray-800/50 border border-gray-700 rounded-lg sm:rounded-xl p-2 sm:p-4 text-center">
                                <div className="text-yellow-400 mb-1 flex justify-center">
                                    <FaClock size={16} className="sm:size-5" />
                                </div>
                                <div className="text-xl sm:text-2xl font-bold text-gray-100">{60 - timeLeft}s</div>
                                <div className="text-xs text-gray-400">Time Taken</div>
                            </div>
                            <div className="bg-gray-800/50 border border-gray-700 rounded-lg sm:rounded-xl p-2 sm:p-4 text-center">
                                <div className="text-green-400 mb-1 flex justify-center">
                                    <FaAward size={16} className="sm:size-5" />
                                </div>
                                <div className="text-xl sm:text-2xl font-bold text-gray-100">{percentage}%</div>
                                <div className="text-xs text-gray-400">Accuracy</div>
                            </div>
                        </div>

                        <div className="mb-4 sm:mb-6 px-2 sm:px-0">
                            <div className="flex justify-between text-xs text-gray-400 mb-1">
                                <span>Performance</span>
                                <span>{percentage}%</span>
                            </div>
                            <div className="w-full bg-gray-800 rounded-full h-1.5 sm:h-2">
                                <div
                                    className={`h-1.5 sm:h-2 rounded-full ${percentage >= 80 ? 'bg-green-500' : percentage >= 50 ? 'bg-yellow-500' : 'bg-red-500'}`}
                                    style={{ width: `${percentage}%` }}
                                ></div>
                            </div>
                        </div>

                        <div className="flex justify-center px-2 sm:px-0">
                            <button
                                onClick={resetExam}
                                className="px-4 py-2 sm:px-6 sm:py-3 bg-gradient-to-r from-blue-600 to-indigo-600 text-sm sm:text-base text-white rounded-lg sm:rounded-xl hover:from-blue-700 hover:to-indigo-700 transition-all font-medium flex items-center shadow-lg hover:shadow-blue-500/20"
                            >
                                <FaRedo className="mr-1 sm:mr-2" /> Restart Exam
                            </button>
                        </div>
                    </motion.div>
                </div>
            </div>
        );
    }

    return (
        <div className="h-auto min-h-[28rem] max-w-full md:max-w-2xl mx-auto bg-gradient-to-br from-gray-900 to-gray-800 rounded-xl border border-gray-700 overflow-hidden shadow-2xl flex flex-col">
            {/* Terminal-style header */}
            <div className="flex items-center px-4 md:px-6 py-3 md:py-4 bg-gray-800/50 border-b border-gray-700 z-50">
                <div className="flex space-x-2 mr-3 md:mr-4">
                    <div className="w-2 h-2 md:w-3 md:h-3 rounded-full bg-red-500"></div>
                    <div className="w-2 h-2 md:w-3 md:h-3 rounded-full bg-yellow-500"></div>
                    <div className="w-2 h-2 md:w-3 md:h-3 rounded-full bg-green-500"></div>
                </div>
                <div className="text-xs md:text-sm text-gray-300 font-mono flex items-center truncate">
                    <FaTerminal className="mr-1 md:mr-2 text-blue-400" />
                    <span className="text-gray-500 truncate">{currentUrl}</span>
                    <span className="text-gray-500 truncate">exam/q{currentQuestionIndex + 1}</span>
                </div>
                <div className="ml-auto flex items-center space-x-2 md:space-x-4">
                    <div className="text-xs text-gray-500 font-mono hidden sm:flex items-center">
                        {new Date().toLocaleTimeString('en-US', {
                            hour: '2-digit',
                            minute: '2-digit',
                            second: '2-digit',
                            hour12: true
                        })}
                    </div>

                    <button
                        onClick={resetExam}
                        className="relative p-1 md:p-2 group"
                        title="Reset"
                    >
                        {/* Circular background (only shows on hover) */}
                        <div className="absolute inset-0 bg-gray-700/30 rounded-full scale-0 group-hover:scale-100 opacity-0 group-hover:opacity-100 transition-all duration-200" />

                        {/* Infinite rotation on hover */}
                        <div className="relative text-gray-400 group-hover:text-blue-400 transition-colors duration-200">
                            <FaRedo className="text-xs md:text-sm group-hover:animate-spin" />
                        </div>

                        {/* Click ripple effect */}
                        <span className="absolute inset-0 rounded-full bg-blue-500/10 scale-0 group-active:scale-150 group-active:opacity-0 transition-all duration-600" />
                    </button>
                </div>
            </div>

            {/* Exam content */}
            <div className="p-4 md:p-6 flex-grow flex flex-col">
                {/* Exam info header */}
                <div className="flex flex-row justify-between items-start sm:items-center mb-4 md:mb-6 gap-2">
                    <div>
                        <h2 className="text-lg md:text-xl font-bold text-gray-100 flex items-center">
                            <span className="bg-gradient-to-r from-blue-500 to-indigo-500 text-transparent bg-clip-text">
                                QCBT
                            </span>
                            <span className="ml-2 text-xs bg-blue-500/20 text-blue-400 px-2 py-0.5 md:py-1 rounded-full">
                                PRO MODE
                            </span>
                        </h2>
                        <p className="text-xs text-gray-400 mt-1">
                            Question {currentQuestionIndex + 1} of {questions.length}
                        </p>
                    </div>
                    <div className={`w-16 md:w-20 px-2 py-1 md:px-3 md:py-1.5 rounded-lg text-xs md:text-sm font-mono font-medium flex items-center ${timeLeft > 10 ? 'bg-gray-800/50 text-red-400 border border-gray-700' : 'animate-pulse bg-red-900/50 text-red-200 border border-red-700'}`}>
                        <FaClock className="mr-1 md:mr-2" />
                        {timeLeft}s
                    </div>
                </div>

                {/* Progress indicators */}
                <div className="mb-3 md:mb-4">
                    <div className="flex justify-between text-xs text-gray-500 mb-1">
                        <span>Progress</span>
                        <span>{Math.round(((currentQuestionIndex + 1) / questions.length) * 100)}%</span>
                    </div>
                    <div className="w-full bg-gray-800 rounded-full h-1 md:h-1.5 mb-1 md:mb-2">
                        <div
                            className="bg-gradient-to-r from-blue-500 to-indigo-500 h-full rounded-full"
                            style={{ width: `${((currentQuestionIndex + 1) / questions.length) * 100}%` }}
                        ></div>
                    </div>
                    <div className="w-full bg-gray-800 rounded-full h-1 md:h-1.5">
                        <div
                            className="bg-gradient-to-r from-red-500 to-orange-500 h-full rounded-full"
                            style={{ width: `${((timeLeft) / 60) * 100}%` }}
                        ></div>
                    </div>
                </div>

                {/* Question */}
                <div className="mb-2 flex-grow overflow-y-auto">
                    <motion.h3
                        key={currentQuestion.id}
                        initial={{ opacity: 0, y: 10 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.3 }}
                        className="text-sm md:text-base font-medium text-gray-100 mb-3 md:mb-4"
                    >
                        {currentQuestion.text}
                    </motion.h3>

                    <div className="space-y-2">
                        {currentQuestion.options.map((option) => {
                            const isSelected = selectedOption === option.id;
                            const isCorrectOption = option.id === currentQuestion.correctAnswer;
                            const isSubmitted = currentAnswer?.submitted;
                            const showCorrect = isSubmitted && isCorrectOption;
                            const showIncorrect = isSubmitted && isSelected && !isCorrectOption;

                            return (
                                <motion.div
                                    key={option.id}
                                    whileTap={!isSubmitted ? { scale: 0.98 } : {}}
                                    onClick={() => handleOptionSelect(option.id)}
                                    className={`p-2 md:p-3 border rounded-lg cursor-pointer transition-all ${isSelected ? 'border-blue-500 bg-gray-800/50' : 'border-gray-700 hover:border-gray-600 bg-gray-800/30'
                                        } ${showCorrect ? '!border-green-500 !bg-green-500/10' : ''
                                        } ${showIncorrect ? '!border-red-500 !bg-red-500/10' : ''
                                        }`}
                                >
                                    <div className="flex items-center">
                                        <div
                                            className={`w-4 h-4 md:w-5 md:h-5 rounded-full border mr-2 md:mr-3 flex items-center justify-center transition-all ${isSelected ? 'border-blue-500 bg-blue-500/20 text-blue-400' : 'border-gray-600'
                                                } ${showCorrect ? '!border-green-500 !bg-green-500/20 !text-green-400' : ''
                                                } ${showIncorrect ? '!border-red-500 !bg-red-500/20 !text-red-400' : ''
                                                }`}
                                        >
                                            {isSelected && !isSubmitted && (
                                                <motion.div
                                                    initial={{ scale: 0 }}
                                                    animate={{ scale: 1 }}
                                                    className="w-2 h-2 md:w-2.5 md:h-2.5 rounded-full bg-blue-400"
                                                />
                                            )}
                                            {showCorrect && <FaCheck className="text-xs" />}
                                            {showIncorrect && <FaTimes className="text-xs" />}
                                        </div>
                                        <span className={`text-xs md:text-sm ${isSelected ? 'text-gray-100' : 'text-gray-300'}`}>
                                            {option.text}
                                        </span>
                                    </div>
                                </motion.div>
                            );
                        })}
                    </div>
                </div>

                {/* Navigation controls */}
                <div className="flex flex-col-reverse sm:flex-row justify-between items-center pt-2 border-t border-gray-800 gap-2 md:gap-0">
                    <div className="flex space-x-2 md:space-x-3 w-full sm:w-auto">
                        <button
                            onClick={() => handleQuestionChange('prev')}
                            disabled={currentQuestionIndex === 0}
                            className="px-3 py-1.5 md:px-4 md:py-2 text-xs md:text-sm bg-gray-800/50 text-gray-300 rounded-lg border border-gray-700 disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-700 transition-all flex items-center flex-1 sm:flex-none justify-center"
                        >
                            <FaArrowLeft className="mr-1 md:mr-2" /> Previous
                        </button>
                        <button
                            onClick={() => handleQuestionChange('next')}
                            disabled={currentQuestionIndex === questions.length - 1}
                            className="px-3 py-1.5 md:px-4 md:py-2 text-xs md:text-sm bg-gray-800/50 text-gray-300 rounded-lg border border-gray-700 disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-700 transition-all flex items-center flex-1 sm:flex-none justify-center"
                        >
                            Next <FaArrowRight className="ml-1 md:ml-2" />
                        </button>
                    </div>

                    <AnimatePresence>
                        {/* Save button for all questions (including last) */}
                        {showSaveButton && (
                            <motion.button
                                onClick={handleSaveAnswer}
                                className="px-4 py-1.5 md:px-5 md:py-2 text-xs md:text-sm bg-gradient-to-r from-blue-600 to-indigo-600 text-white rounded-lg hover:from-blue-700 hover:to-indigo-700 transition-all shadow-md hover:shadow-blue-500/20 w-full sm:w-auto text-center"
                            >
                                Save Answer
                            </motion.button>
                        )}

                        {/* Next button after saving (except last question) */}
                        {showNextButton && (
                            <motion.button
                                onClick={() => handleQuestionChange('next')}
                                className="px-4 py-1.5 md:px-5 md:py-2 text-xs md:text-sm bg-gradient-to-r from-blue-600 to-indigo-600 text-white rounded-lg hover:from-blue-700 hover:to-indigo-700 transition-all shadow-md hover:shadow-blue-500/20 w-full sm:w-auto text-center"
                            >
                                Next Question
                            </motion.button>
                        )}

                        {/* Submit button (only after saving last answer) */}
                        {showSubmitButton && allQuestionsAnswered && (
                            <motion.button
                                onClick={handleSubmitExam}
                                className="px-4 py-1.5 md:px-5 md:py-2 text-xs md:text-sm bg-gradient-to-r from-green-600 to-teal-600 text-white rounded-lg hover:from-green-700 hover:to-teal-700 transition-all shadow-md hover:shadow-green-500/20 w-full sm:w-auto text-center"
                            >
                                Submit Exam
                            </motion.button>
                        )}
                    </AnimatePresence>
                </div>
            </div>
        </div>
    );
};

export default ExamTerminal;